import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Toast, { ToastType } from '../components/ui/Toast';
import { Plus, X, Save, RefreshCw, Trash2, Settings, MessageSquare, Zap, BarChart3, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { handleApiError } from '../utils/errorHandling';
import {
  RAGGuidelines,
  RAGCustomResponseRule,
  RAGTaskCapability,
  PrebuiltMessage,
  PrebuiltMessageCategory,
  AnalyticsGuidelines
} from '../types';

type TabType = 'guidelines' | 'custom-rules' | 'tasks' | 'prebuilt-messages' | 'analytics-guidelines';

const AIConfigurationPage: React.FC = () => {
  const { setSubscriptionError } = useSubscriptionError();
  const { isSuperAdmin } = useImpersonation();
  const [activeTab, setActiveTab] = useState<TabType>('guidelines');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  
  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  // RAG Guidelines State
  const [guidelines, setGuidelines] = useState<Partial<RAGGuidelines>>({});

  // Custom Rules State
  const [customRules, setCustomRules] = useState<RAGCustomResponseRule[]>([]);
  const [editingRule, setEditingRule] = useState<Partial<RAGCustomResponseRule> | null>(null);

  // Task Capabilities State
  const [capabilities, setCapabilities] = useState<RAGTaskCapability[]>([]);
  const [editingCapability, setEditingCapability] = useState<Partial<RAGTaskCapability> | null>(null);

  // Prebuilt Messages State
  const [prebuiltMessages, setPrebuiltMessages] = useState<PrebuiltMessage[]>([]);
  const [editingMessage, setEditingMessage] = useState<Partial<PrebuiltMessage> | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PrebuiltMessageCategory>('out_of_context');

  // Analytics Guidelines State
  const [analyticsGuidelines, setAnalyticsGuidelines] = useState<Partial<AnalyticsGuidelines>>({});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'guidelines':
          await loadGuidelines();
          break;
        case 'custom-rules':
          await loadCustomRules();
          break;
        case 'tasks':
          await loadCapabilities();
          break;
        case 'prebuilt-messages':
          await loadPrebuiltMessages();
          break;
        case 'analytics-guidelines':
          await loadAnalyticsGuidelines();
          break;
      }
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to load data: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RAG Guidelines ====================
  const loadGuidelines = async () => {
    const data = await api.getRAGGuidelines();
    if (data) {
      setGuidelines(data);
    } else {
      // Load defaults if no guidelines exist
      const defaults = await api.getRAGDefaultGuidelines();
      setGuidelines(defaults);
    }
  };

  const loadDefaults = async () => {
    try {
      const defaults = await api.getRAGDefaultGuidelines();
      setGuidelines(defaults);
      showToast('Default guidelines loaded', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to load defaults: ${m}`, 'error'), setSubscriptionError);
    }
  };

  const saveGuidelines = async () => {
    setSaving(true);
    try {
      const updated = await api.upsertRAGGuidelines(guidelines);
      setGuidelines(updated);
      showToast('Guidelines saved successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to save guidelines: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setSaving(false);
    }
  };

  // ==================== Custom Rules ====================
  const loadCustomRules = async () => {
    const rules = await api.listRAGCustomRules();
    setCustomRules(rules);
  };

  const saveCustomRule = async () => {
    if (!editingRule || !editingRule.ruleId || !editingRule.match || !editingRule.action) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      await api.upsertRAGCustomRule(editingRule as RAGCustomResponseRule);
      await loadCustomRules();
      setEditingRule(null);
      showToast('Custom rule saved successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to save rule: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await api.deleteRAGCustomRule(ruleId);
      await loadCustomRules();
      showToast('Rule deleted successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to delete rule: ${m}`, 'error'), setSubscriptionError);
    }
  };

  // ==================== Task Capabilities ====================
  const loadCapabilities = async () => {
    const caps = await api.listRAGTaskCapabilities();
    setCapabilities(caps);
  };

  const saveCapability = async () => {
    if (!editingCapability || !editingCapability.id || !editingCapability.name || !editingCapability.modality) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      console.log('Saving capability:', JSON.stringify(editingCapability, null, 2));
      const result = await api.upsertRAGTaskCapability(editingCapability as RAGTaskCapability);
      console.log('Save result:', JSON.stringify(result, null, 2));
      await loadCapabilities();
      setEditingCapability(null);
      showToast('Task capability saved successfully!', 'success');
    } catch (err: any) {
      console.error('Save error:', err);
      handleApiError(err, (m) => showToast(`Failed to save capability: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setSaving(false);
    }
  };

  const deleteCapability = async (id: string) => {
    if (!confirm('Are you sure you want to delete this capability?')) return;
    
    try {
      await api.deleteRAGTaskCapability(id);
      await loadCapabilities();
      showToast('Capability deleted successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to delete capability: ${m}`, 'error'), setSubscriptionError);
    }
  };

  // ==================== Prebuilt Messages ====================
  const loadPrebuiltMessages = async () => {
    const messages = await api.listPrebuiltMessages({ category: selectedCategory });
    setPrebuiltMessages(messages);
  };

  const savePrebuiltMessage = async () => {
    if (!editingMessage || !editingMessage.category || !editingMessage.messageText) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingMessage._id) {
        await api.updatePrebuiltMessage(editingMessage._id, editingMessage);
      } else {
        await api.createPrebuiltMessage(editingMessage as Omit<PrebuiltMessage, '_id' | 'tenant' | 'usageCount' | 'createdAt' | 'updatedAt'>);
      }
      await loadPrebuiltMessages();
      setEditingMessage(null);
      showToast('Message saved successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to save message: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setSaving(false);
    }
  };

  const deletePrebuiltMessage = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await api.deletePrebuiltMessage(messageId);
      await loadPrebuiltMessages();
      showToast('Message deleted successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to delete message: ${m}`, 'error'), setSubscriptionError);
    }
  };

  // ==================== Analytics Guidelines ====================
  const loadAnalyticsGuidelines = async () => {
    const data = await api.getAnalyticsGuidelines();
    setAnalyticsGuidelines(data || {});
  };

  const saveAnalyticsGuidelines = async () => {
    setSaving(true);
    try {
      const updated = await api.upsertAnalyticsGuidelines(analyticsGuidelines);
      setAnalyticsGuidelines(updated);
      showToast('Analytics guidelines saved successfully!', 'success');
    } catch (err: any) {
      handleApiError(err, (m) => showToast(`Failed to save analytics guidelines: ${m}`, 'error'), setSubscriptionError);
    } finally {
      setSaving(false);
    }
  };

  // ==================== Render Functions ====================

  const renderGuidelines = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-gray-900">RAG Guidelines</h2>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">Configure AI behavior, planner and final instructions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadDefaults} variant="secondary" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Load Defaults
          </Button>
          <Button onClick={saveGuidelines} isLoading={saving} size="sm">
            <Save className="w-4 h-4 mr-2" />
            Save Guidelines
          </Button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Planner Instructions</label>
        <textarea
          value={guidelines.planner_instructions || ''}
          onChange={(e) => setGuidelines({ ...guidelines, planner_instructions: e.target.value })}
          placeholder="Instructions for the planning stage..."
          rows={6}
          className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Final Instructions</label>
        <textarea
          value={guidelines.final_instructions || ''}
          onChange={(e) => setGuidelines({ ...guidelines, final_instructions: e.target.value })}
          placeholder="Instructions for the final response generation..."
          rows={6}
          className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">No Context Message</label>
        <Input
          value={guidelines.no_context_message || ''}
          onChange={(e) => setGuidelines({ ...guidelines, no_context_message: e.target.value })}
          placeholder="Message to display when no context is found..."
        />
      </div>

    </div>
  );

  const renderCustomRules = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-gray-900">Custom Response Rules</h2>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">Create pattern-based rules to override or modify AI responses</p>
        </div>
        <Button onClick={() => setEditingRule({
          ruleId: `rule_${Date.now()}`,
          priority: 100,
          match: { type: 'substring', patterns: [''], case_sensitive: false },
          action: { type: 'override', content: '', content_type: 'text' },
          isActive: true
        })} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {customRules.length === 0 ? (
          <div className="text-center py-12 dark:bg-surface bg-gray-50 rounded-lg border-2 border-dashed border-border-color">
            <AlertCircle className="w-12 h-12 mx-auto dark:text-gray-600 text-gray-400 mb-3" />
            <p className="dark:text-gray-400 text-gray-600">No custom rules configured</p>
            <p className="text-sm dark:text-gray-500 text-gray-500 mt-1">Click "Add Rule" to create your first rule</p>
          </div>
        ) : (
          customRules.map((rule) => (
            <Card key={rule.ruleId} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold dark:text-white text-gray-900">{rule.ruleId}</h4>
                    <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                      Priority: {rule.priority || 100}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${rule.isActive ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="dark:text-gray-400 text-gray-600">
                      <span className="font-medium">Match Type:</span> {rule.match.type}
                    </p>
                    <p className="dark:text-gray-400 text-gray-600">
                      <span className="font-medium">Patterns:</span> {rule.match.patterns.join(', ')}
                    </p>
                    <p className="dark:text-gray-400 text-gray-600">
                      <span className="font-medium">Action:</span> {rule.action.type} - {rule.action.content_type}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEditingRule(rule)}
                    variant="secondary"
                    size="sm"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => deleteCustomRule(rule.ruleId)}
                    variant="secondary"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="dark:bg-background bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold dark:text-white text-gray-900">
                  {editingRule.ruleId?.startsWith('rule_') ? 'Add Custom Rule' : 'Edit Custom Rule'}
                </h3>
                <button
                  onClick={() => setEditingRule(null)}
                  className="p-2 hover:bg-surface rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Rule ID *</label>
                  <Input
                    value={editingRule.ruleId || ''}
                    onChange={(e) => setEditingRule({ ...editingRule, ruleId: e.target.value })}
                    placeholder="unique-rule-id"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Priority</label>
                  <Input
                    type="number"
                    value={editingRule.priority || 100}
                    onChange={(e) => setEditingRule({ ...editingRule, priority: parseInt(e.target.value) || 100 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Match Type *</label>
                  <select
                    value={editingRule.match?.type || 'substring'}
                    onChange={(e) => setEditingRule({ 
                      ...editingRule, 
                      match: { ...editingRule.match!, type: e.target.value as any }
                    })}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  >
                    <option value="exact">Exact</option>
                    <option value="substring">Substring</option>
                    <option value="regex">Regex</option>
                    <option value="keywords_any">Keywords Any</option>
                    <option value="keywords_all">Keywords All</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                    Patterns * <span className="text-xs text-gray-500">(comma-separated)</span>
                  </label>
                  <Input
                    value={editingRule.match?.patterns?.join(', ') || ''}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      match: { ...editingRule.match!, patterns: e.target.value.split(',').map(p => p.trim()).filter(p => p) }
                    })}
                    placeholder="pattern1, pattern2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Action Type *</label>
                  <select
                    value={editingRule.action?.type || 'override'}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      action: { ...editingRule.action!, type: e.target.value as any }
                    })}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  >
                    <option value="override">Override</option>
                    <option value="prepend">Prepend</option>
                    <option value="append">Append</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Content Type</label>
                  <select
                    value={editingRule.action?.content_type || 'text'}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      action: { ...editingRule.action!, content_type: e.target.value as any }
                    })}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  >
                    <option value="text">Text</option>
                    <option value="markdown">Markdown</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Content *</label>
                  <textarea
                    value={editingRule.action?.content || ''}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      action: { ...editingRule.action!, content: e.target.value }
                    })}
                    placeholder="Response content..."
                    rows={4}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingRule.isActive !== false}
                      onChange={(e) => setEditingRule({ ...editingRule, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium dark:text-gray-300 text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border-color">
                <Button onClick={() => setEditingRule(null)} variant="secondary">
                  Cancel
                </Button>
                <Button onClick={saveCustomRule} isLoading={saving}>
                  Save Rule
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTasks = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-gray-900">Task Capabilities</h2>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">Define executable tasks and actions the AI can perform</p>
        </div>
        <Button onClick={() => setEditingCapability({
          id: '',
          name: '',
          modality: 'frontend',
          description: '',
          confirm_required: false,
          isActive: true,
          safety_tags: [],
          classification_categories: [],
          redact_log_keys: []
        })} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Capability
        </Button>
      </div>

      <div className="space-y-3">
        {capabilities.length === 0 ? (
          <div className="text-center py-12 dark:bg-surface bg-gray-50 rounded-lg border-2 border-dashed border-border-color">
            <Zap className="w-12 h-12 mx-auto dark:text-gray-600 text-gray-400 mb-3" />
            <p className="dark:text-gray-400 text-gray-600">No task capabilities configured</p>
            <p className="text-sm dark:text-gray-500 text-gray-500 mt-1">Click "Add Capability" to create your first task</p>
          </div>
        ) : (
          capabilities.map((cap) => (
            <Card key={cap.id} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="font-semibold dark:text-white text-gray-900">{cap.name}</h4>
                    <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                      {cap.modality}
                    </span>
                    {cap.confirm_required && (
                      <span className="text-xs px-2 py-1 rounded bg-yellow-500/10 text-yellow-600">
                        Confirm Required
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${cap.isActive ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                      {cap.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="dark:text-gray-400 text-gray-600">
                      <span className="font-medium">ID:</span> {cap.id}
                    </p>
                    {cap.description && (
                      <p className="dark:text-gray-400 text-gray-600">{cap.description}</p>
                    )}
                    {cap.modality === 'backend' && cap.endpoint_id && (
                      <p className="dark:text-gray-400 text-gray-600">
                        <span className="font-medium">Endpoint:</span> {cap.endpoint_id}
                      </p>
                    )}
                    {cap.id === 'builtin.search' && cap.search_options?.extra_vector_ratio != null && (
                      <p className="dark:text-gray-400 text-gray-600">
                        <span className="font-medium">Extra Vector Ratio:</span> {cap.search_options.extra_vector_ratio}
                      </p>
                    )}
                    {cap.modality === 'frontend' && cap.callback_id && (
                      <p className="dark:text-gray-400 text-gray-600">
                        <span className="font-medium">Callback:</span> {cap.callback_id}
                      </p>
                    )}
                    {cap.classification_categories && cap.classification_categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {cap.classification_categories.map((cat, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                    {cap.safety_tags && cap.safety_tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {cap.safety_tags.map((tag, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-500">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEditingCapability(cap)}
                    variant="secondary"
                    size="sm"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => deleteCapability(cap.id)}
                    variant="secondary"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Edit Modal - Enhanced */}
      {editingCapability && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="dark:bg-background bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold dark:text-white text-gray-900">
                  {!editingCapability.id ? 'Add Task Capability' : 'Edit Task Capability'}
                </h3>
                <button
                  onClick={() => setEditingCapability(null)}
                  className="p-2 hover:bg-surface rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Task ID *</label>
                  <Input
                    value={editingCapability.id || ''}
                    onChange={(e) => setEditingCapability({ ...editingCapability, id: e.target.value })}
                    placeholder="e.g., cart.addItem, ui.product_grid"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Name *</label>
                  <Input
                    value={editingCapability.name || ''}
                    onChange={(e) => setEditingCapability({ ...editingCapability, name: e.target.value })}
                    placeholder="Human-readable name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Modality *</label>
                  <select
                    value={editingCapability.modality || 'frontend'}
                    onChange={(e) => setEditingCapability({ ...editingCapability, modality: e.target.value as 'frontend' | 'backend' })}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  >
                    <option value="frontend">Frontend (UI action)</option>
                    <option value="backend">Backend (API call)</option>
                  </select>
                </div>
                <div className="flex items-center gap-6 pt-8">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingCapability.confirm_required === true}
                      onChange={(e) => setEditingCapability({ ...editingCapability, confirm_required: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium dark:text-gray-300 text-gray-700">Confirm Required</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingCapability.isActive !== false}
                      onChange={(e) => setEditingCapability({ ...editingCapability, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium dark:text-gray-300 text-gray-700">Active</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editingCapability.description || ''}
                    onChange={(e) => setEditingCapability({ ...editingCapability, description: e.target.value })}
                    placeholder="What does this task do?"
                    rows={2}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  />
                </div>
              </div>

              {/* Frontend-specific fields */}
              {editingCapability.modality === 'frontend' && (
                <div className="border-t border-border-color pt-4">
                  <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">Frontend Configuration</h4>
                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Callback ID</label>
                    <Input
                      value={editingCapability.callback_id || ''}
                      onChange={(e) => setEditingCapability({ ...editingCapability, callback_id: e.target.value })}
                      placeholder="e.g., cart.addItem (SDK callback)"
                    />
                  </div>
                </div>
              )}

              {/* Search-specific options (builtin.search) */}
              {editingCapability.id === 'builtin.search' && (
                <div className="border-t border-border-color pt-4">
                  <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">
                    Search Options
                    <span className="ml-2 text-xs font-normal text-gray-500">Hybrid search tuning (current value: {editingCapability.search_options?.extra_vector_ratio ?? 'undefined'})</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                        Extra Vector Ratio
                        <span className="ml-2 text-xs font-normal text-gray-500">(0–1, default 1)</span>
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={
                          editingCapability.search_options?.extra_vector_ratio ?? ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          let value: number | undefined = undefined;
                          if (raw !== '') {
                            const parsed = parseFloat(raw);
                            if (!isNaN(parsed) && isFinite(parsed)) {
                              value = parsed;
                            }
                          }
                          setEditingCapability({
                            ...editingCapability,
                            search_options: {
                              ...(editingCapability.search_options || {}),
                              extra_vector_ratio: value
                            }
                          });
                        }}
                        placeholder="e.g., 0.33"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Extra unfiltered vector results = ceil(hybridCount × ratio).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Backend-specific fields */}
              {editingCapability.modality === 'backend' && (
                <div className="border-t border-border-color pt-4">
                  <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">Backend Configuration</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Endpoint URL</label>
                      <Input
                        value={editingCapability.endpoint_id || ''}
                        onChange={(e) => setEditingCapability({ ...editingCapability, endpoint_id: e.target.value })}
                        placeholder="https://api.example.com/webhook"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">HTTP Method</label>
                      <select
                        value={editingCapability.endpoint_options?.method || 'POST'}
                        onChange={(e) => setEditingCapability({
                          ...editingCapability,
                          endpoint_options: { ...editingCapability.endpoint_options, method: e.target.value as any }
                        })}
                        className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                        <option value="PATCH">PATCH</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Body Mode</label>
                      <select
                        value={editingCapability.endpoint_options?.body_mode || 'raw'}
                        onChange={(e) => setEditingCapability({
                          ...editingCapability,
                          endpoint_options: { ...editingCapability.endpoint_options, body_mode: e.target.value as any }
                        })}
                        className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                      >
                        <option value="raw">Raw (JSON)</option>
                        <option value="form-data">Form Data</option>
                        <option value="x-www-form-urlencoded">URL Encoded</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-border-color pt-4">
                <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">
                  Contract DDL
                  <span className="ml-2 text-xs font-normal text-gray-500">Planner-facing task definition</span>
                </h4>
                <textarea
                  value={editingCapability.contract_ddl || ''}
                  onChange={(e) => setEditingCapability({ ...editingCapability, contract_ddl: e.target.value })}
                  placeholder="TASK task.id(param: type) RETURNS Result -- Description"
                  rows={6}
                  className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-sm"
                />
              </div>

              {/* Field Definition Templates (for search capabilities) */}
              {editingCapability.id === 'builtin.search' && (
                <div className="border-t border-border-color pt-4">
                  <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">
                    Field Definition Templates
                    <span className="ml-2 text-xs font-normal text-gray-500">Dynamic field line templates (use {`{{TYPE_NAME}}`}, {`{{FIELD_NAMES}}`}, {`{{RECORD_TYPES}}`})</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium dark:text-gray-400 text-gray-600 mb-1">Boolean Field Line Template</label>
                      <input
                        type="text"
                        value={editingCapability.field_definition_template?.boolean_field_line || ''}
                        onChange={(e) => setEditingCapability({
                          ...editingCapability,
                          field_definition_template: {
                            ...editingCapability.field_definition_template,
                            boolean_field_line: e.target.value,
                            numerical_field_line: editingCapability.field_definition_template?.numerical_field_line || '',
                            record_types_line: editingCapability.field_definition_template?.record_types_line || ''
                          }
                        })}
                        placeholder='For "{{TYPE_NAME}}", here are the possible <boolean field> names: {{FIELD_NAMES}}'
                        className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium dark:text-gray-400 text-gray-600 mb-1">Numerical Field Line Template</label>
                      <input
                        type="text"
                        value={editingCapability.field_definition_template?.numerical_field_line || ''}
                        onChange={(e) => setEditingCapability({
                          ...editingCapability,
                          field_definition_template: {
                            ...editingCapability.field_definition_template,
                            boolean_field_line: editingCapability.field_definition_template?.boolean_field_line || '',
                            numerical_field_line: e.target.value,
                            record_types_line: editingCapability.field_definition_template?.record_types_line || ''
                          }
                        })}
                        placeholder='For "{{TYPE_NAME}}", here are the possible <numerical field> names: {{FIELD_NAMES}}'
                        className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium dark:text-gray-400 text-gray-600 mb-1">Record Types Line Template</label>
                      <input
                        type="text"
                        value={editingCapability.field_definition_template?.record_types_line || ''}
                        onChange={(e) => setEditingCapability({
                          ...editingCapability,
                          field_definition_template: {
                            ...editingCapability.field_definition_template,
                            boolean_field_line: editingCapability.field_definition_template?.boolean_field_line || '',
                            numerical_field_line: editingCapability.field_definition_template?.numerical_field_line || '',
                            record_types_line: e.target.value
                          }
                        })}
                        placeholder='Here are the possible <record type> names: {{RECORD_TYPES}}'
                        className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Parameters Schema */}
              <div className="border-t border-border-color pt-4">
                <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">
                  Parameters Schema (JSON Schema)
                  <span className="ml-2 text-xs font-normal text-gray-500">Define the parameters this task accepts</span>
                </h4>
                <textarea
                  value={editingCapability.params_schema ? JSON.stringify(editingCapability.params_schema, null, 2) : ''}
                  onChange={(e) => {
                    try {
                      const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                      setEditingCapability({ ...editingCapability, params_schema: parsed });
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  placeholder={'{\n  "type": "object",\n  "properties": {\n    "sku": { "type": "string" },\n    "qty": { "type": "integer", "minimum": 1 }\n  },\n  "required": ["sku"]\n}'}
                  rows={6}
                  className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-sm"
                />
              </div>

              <div className="border-t border-border-color pt-4">
                <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">
                  Response Schema (JSON Schema)
                  <span className="ml-2 text-xs font-normal text-gray-500">Describe the response payload</span>
                </h4>
                <textarea
                  value={editingCapability.response_schema ? JSON.stringify(editingCapability.response_schema, null, 2) : ''}
                  onChange={(e) => {
                    try {
                      const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                      setEditingCapability({ ...editingCapability, response_schema: parsed });
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  placeholder={'{\n  "type": "object",\n  "properties": {\n    "status": { "type": "string" }\n  }\n}'}
                  rows={6}
                  className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900 font-mono text-sm"
                />
              </div>

              {/* Classification & Tags */}
              <div className="border-t border-border-color pt-4">
                <h4 className="text-sm font-semibold dark:text-gray-300 text-gray-700 mb-3">Classification & Tags</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                      Classification Categories
                      <span className="ml-2 text-xs font-normal text-gray-500">(comma-separated)</span>
                    </label>
                    <Input
                      value={editingCapability.classification_categories?.join(', ') || ''}
                      onChange={(e) => setEditingCapability({
                        ...editingCapability,
                        classification_categories: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                      })}
                      placeholder="catalog.product_discovery, commerce.checkout"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                      Safety Tags
                      <span className="ml-2 text-xs font-normal text-gray-500">(comma-separated)</span>
                    </label>
                    <Input
                      value={editingCapability.safety_tags?.join(', ') || ''}
                      onChange={(e) => setEditingCapability({
                        ...editingCapability,
                        safety_tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                      })}
                      placeholder="read_only, pii_read, financial"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                      Redact Log Keys
                      <span className="ml-2 text-xs font-normal text-gray-500">(comma-separated keys to hide in logs)</span>
                    </label>
                    <Input
                      value={editingCapability.redact_log_keys?.join(', ') || ''}
                      onChange={(e) => setEditingCapability({
                        ...editingCapability,
                        redact_log_keys: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                      })}
                      placeholder="password, credit_card, ssn"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border-color">
                <Button onClick={() => setEditingCapability(null)} variant="secondary">
                  Cancel
                </Button>
                <Button onClick={saveCapability} isLoading={saving}>
                  Save Capability
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPrebuiltMessages = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-gray-900">Prebuilt Messages</h2>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">Manage template responses for various scenarios</p>
        </div>
        <Button onClick={() => setEditingMessage({
          category: selectedCategory,
          messageText: '',
          messageType: 'generic',
          priority: 5,
          isActive: true,
          metadata: {}
        })} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Message
        </Button>
      </div>

      {/* Category Filter */}
      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Category</label>
        <select
          value={selectedCategory}
          onChange={(e) => {
            setSelectedCategory(e.target.value as PrebuiltMessageCategory);
            loadPrebuiltMessages();
          }}
          className="w-full max-w-xs px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        >
          <option value="out_of_context">Out of Context</option>
          <option value="no_results">No Results</option>
          <option value="error">Error</option>
          <option value="greeting">Greeting</option>
          <option value="farewell">Farewell</option>
          <option value="fallback">Fallback</option>
        </select>
      </div>

      <div className="space-y-3">
        {prebuiltMessages.length === 0 ? (
          <div className="text-center py-12 dark:bg-surface bg-gray-50 rounded-lg border-2 border-dashed border-border-color">
            <MessageSquare className="w-12 h-12 mx-auto dark:text-gray-600 text-gray-400 mb-3" />
            <p className="dark:text-gray-400 text-gray-600">No messages for this category</p>
            <p className="text-sm dark:text-gray-500 text-gray-500 mt-1">Click "Add Message" to create one</p>
          </div>
        ) : (
          prebuiltMessages.map((msg) => (
            <Card key={msg._id} className="p-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                      Priority: {msg.priority || 5}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-500">
                      {msg.messageType || 'generic'}
                    </span>
                    {msg.metadata?.locale && (
                      <span className="text-xs px-2 py-1 rounded bg-purple-500/10 text-purple-500">
                        {msg.metadata.locale}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${msg.isActive ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                      {msg.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="dark:text-gray-300 text-gray-700">{msg.messageText}</p>
                  {msg.usageCount !== undefined && msg.usageCount > 0 && (
                    <p className="text-xs dark:text-gray-500 text-gray-600 mt-2">
                      Used {msg.usageCount} times
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEditingMessage(msg)}
                    variant="secondary"
                    size="sm"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => deletePrebuiltMessage(msg._id!)}
                    variant="secondary"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="dark:bg-background bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold dark:text-white text-gray-900">
                  {editingMessage._id ? 'Edit Prebuilt Message' : 'Add Prebuilt Message'}
                </h3>
                <button
                  onClick={() => setEditingMessage(null)}
                  className="p-2 hover:bg-surface rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Category *</label>
                  <select
                    value={editingMessage.category}
                    onChange={(e) => setEditingMessage({ ...editingMessage, category: e.target.value as PrebuiltMessageCategory })}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  >
                    <option value="out_of_context">Out of Context</option>
                    <option value="no_results">No Results</option>
                    <option value="error">Error</option>
                    <option value="greeting">Greeting</option>
                    <option value="farewell">Farewell</option>
                    <option value="fallback">Fallback</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Message Type</label>
                  <Input
                    value={editingMessage.messageType || 'generic'}
                    onChange={(e) => setEditingMessage({ ...editingMessage, messageType: e.target.value })}
                    placeholder="generic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Priority</label>
                  <Input
                    type="number"
                    value={editingMessage.priority || 5}
                    onChange={(e) => setEditingMessage({ ...editingMessage, priority: parseInt(e.target.value) || 5 })}
                    min="1"
                    max="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Locale</label>
                  <Input
                    value={editingMessage.metadata?.locale || ''}
                    onChange={(e) => setEditingMessage({
                      ...editingMessage,
                      metadata: { ...editingMessage.metadata, locale: e.target.value }
                    })}
                    placeholder="en, el, etc."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Message Text *</label>
                  <textarea
                    value={editingMessage.messageText || ''}
                    onChange={(e) => setEditingMessage({ ...editingMessage, messageText: e.target.value })}
                    placeholder="The message content..."
                    rows={4}
                    className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingMessage.isActive !== false}
                      onChange={(e) => setEditingMessage({ ...editingMessage, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium dark:text-gray-300 text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border-color">
                <Button onClick={() => setEditingMessage(null)} variant="secondary">
                  Cancel
                </Button>
                <Button onClick={savePrebuiltMessage} isLoading={saving}>
                  Save Message
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalyticsGuidelines = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold dark:text-white text-gray-900">Analytics Guidelines</h2>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">Configure AI behavior for analytics queries</p>
        </div>
        <Button onClick={saveAnalyticsGuidelines} isLoading={saving} size="sm">
          <Save className="w-4 h-4 mr-2" />
          Save Guidelines
        </Button>
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">System Instructions</label>
        <textarea
          value={analyticsGuidelines.system || ''}
          onChange={(e) => setAnalyticsGuidelines({ ...analyticsGuidelines, system: e.target.value })}
          placeholder="System-level instructions for analytics queries..."
          rows={4}
          className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Query Planning</label>
        <textarea
          value={analyticsGuidelines.query_planning || ''}
          onChange={(e) => setAnalyticsGuidelines({ ...analyticsGuidelines, query_planning: e.target.value })}
          placeholder="Instructions for planning analytics queries..."
          rows={6}
          className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Generation Guidelines</label>
        <textarea
          value={analyticsGuidelines.generation || ''}
          onChange={(e) => setAnalyticsGuidelines({ ...analyticsGuidelines, generation: e.target.value })}
          placeholder="Guidelines for generating analytics responses..."
          rows={6}
          className="w-full px-3 py-2 dark:bg-surface bg-white border border-border-color rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:text-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">Output Format</label>
        <Input
          value={analyticsGuidelines.output_format || ''}
          onChange={(e) => setAnalyticsGuidelines({ ...analyticsGuidelines, output_format: e.target.value })}
          placeholder="e.g., structured JSON, tables, charts"
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div>
        <h1 className="text-3xl font-bold dark:text-white text-gray-900">AI Configuration</h1>
        <p className="dark:text-text-secondary text-light-text-secondary mt-1">
          Configure AI behavior, tasks, and response templates
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-color">
        <div className="flex space-x-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('guidelines')}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'guidelines'
                ? 'border-primary text-primary'
                : 'border-transparent dark:text-gray-400 text-gray-600 hover:text-primary'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span>RAG Guidelines</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('custom-rules')}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'custom-rules'
                ? 'border-primary text-primary'
                : 'border-transparent dark:text-gray-400 text-gray-600 hover:text-primary'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span>Custom Rules</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'tasks'
                ? 'border-primary text-primary'
                : 'border-transparent dark:text-gray-400 text-gray-600 hover:text-primary'
            }`}
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Task Capabilities</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('prebuilt-messages')}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'prebuilt-messages'
                ? 'border-primary text-primary'
                : 'border-transparent dark:text-gray-400 text-gray-600 hover:text-primary'
            }`}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span>Prebuilt Messages</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics-guidelines')}
            className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
              activeTab === 'analytics-guidelines'
                ? 'border-primary text-primary'
                : 'border-transparent dark:text-gray-400 text-gray-600 hover:text-primary'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span>Analytics Guidelines</span>
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'guidelines' && renderGuidelines()}
        {activeTab === 'custom-rules' && renderCustomRules()}
        {activeTab === 'tasks' && renderTasks()}
        {activeTab === 'prebuilt-messages' && renderPrebuiltMessages()}
        {activeTab === 'analytics-guidelines' && renderAnalyticsGuidelines()}
      </div>
    </div>
  );
};

export default AIConfigurationPage;
